// 360Viewer.ts
import * as THREE from 'three';
import { ViewerScreen } from './ViewerScreen/ViewerScreen';
import {
  onCameraMoveCallback,
  onCameraZoomCallback,
  ViewerMode,
  ViewerScreenDirection,
} from './Types';
import { ViewerObservation } from './ViewerElement/ObservationElement';
import { MouseController } from './MouseController';
import { Walkthrough } from './ViewerElement/WalkthroughElement';
import { LayerType } from './ViewerScreen/LayerManager';

export class ThreeSixtyViewer {
  private readonly EMPTY_IMAGE_URL = 'assets/webglViewer/space.png';
  private readonly leftViewer: ViewerScreen;
  private readonly rightViewer: ViewerScreen;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly container: any;
  private mouseController: MouseController;
  private watermarkText = { name: '', email: '' ,client: ''};
  private watermarkEnabled = false;
  public static viewerMode: ViewerMode = ViewerMode.NORMAL;
  public static isLocked: boolean = false;
  public static isObservationVisible: boolean = true;
  // Optional: host can subscribe to loading events
  public static notifyLoadingState?: (isLoading: boolean, screen: ViewerScreenDirection) => void;
  
  // Loading state tracking
  private baseLoading = false;
  private texturesLoading = false;
  private readonly baseLoadingCallbacks = new Set<(isLoading: boolean) => void>();
  private readonly textureLoadingCallbacks = new Set<(isLoading: boolean) => void>();
  
  // LOD synchronization for split-screen
  private lodSyncEnabled = false;
  private rightLODChangeCallback?: (oldLevel: number, newLevel: number) => void;
  private pendingLeftImage:
    | {
        url: string;
        thumb?: string;
        tiles?: Array<{ level: number; cols_per_face: number; rows_per_face: number }>;
        tileSourceMode?: 'standard' | 'blurred';
        angle: { verticalAngle?: number; horizontalAngle?: number } | null;
      }
    | null = null;
  /**
   * Initializes a ThreeSixtyViewer instance with two
   *
   * ViewerScreen objects and event listeners.
   * @param container - The HTML container for the viewer.
   * @param addObservationCallback - Callback function to handle adding observations.
   */
  constructor(container: HTMLElement) {
    this.container = container;
    this.renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      precision: 'highp',
      powerPreference: 'high-performance'
    });
    this.renderer.sortObjects = false;
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    
    // Optimize renderer settings for seamless tile rendering
    this.renderer.capabilities.precision = 'highp';
    this.renderer.capabilities.logarithmicDepthBuffer = false;

    this.container.appendChild(this.renderer.domElement);

    this.leftViewer = new ViewerScreen(this.container, this.renderer, ViewerScreenDirection.LEFT);
    this.rightViewer = new ViewerScreen(this.container, this.renderer, ViewerScreenDirection.RIGHT);
    this.leftViewer.disable();

   
    this.mouseController = new MouseController(this.leftViewer, this.rightViewer, this.container);
    this.setupEventListeners();
    this.setupLoadingStateTracking();
    this.setupLODSynchronization();
    this.animate();
  }

  /**
   * Loads images onto the left and right viewers, applying textures and optional callbacks.
   * @param rightPointData - Data for right viewer (url and tiles).
   * @param leftPointData - Data for left viewer (url and tiles).
   * @param rightAngle - Initial camera angle for orientation.
   * @param leftOffset - Offset for the left viewer's camera angle.
   * @param callback - Callback function to execute once images are loaded.
   */
  public async loadImage(
    rightPointData?:
      | {
          image_360?: string;
          image_360_thumb?: string;
          tiles?: Array<{ level: number; cols_per_face: number; rows_per_face: number }>;
          tileSourceMode?: 'standard' | 'blurred';
        }
      | string
      | 'empty'
      | null,
    leftPointData?:
      | {
          image_360?: string;
          image_360_thumb?: string;
          tiles?: Array<{ level: number; cols_per_face: number; rows_per_face: number }>;
          tileSourceMode?: 'standard' | 'blurred';
        }
      | string
      | 'empty'
      | null,
    rightAngle?: { verticalAngle?: number; horizontalAngle?: number },
    leftOffset?: number | null,
    callback?: () => void,
  ): Promise<void> {
    // Handle legacy string format and extract data
    let rightUrl: string | null = null;
    let rightThumb: string | undefined;
    let rightTiles: Array<{ level: number; cols_per_face: number; rows_per_face: number }> | undefined;
    let leftUrl: string | null = null;
    let leftThumb: string | undefined;
    let leftTiles: Array<{ level: number; cols_per_face: number; rows_per_face: number }> | undefined;
    let rightTileSourceMode: 'standard' | 'blurred' = 'standard';
    let leftTileSourceMode: 'standard' | 'blurred' = 'standard';

    // Parse right viewer data
    if (typeof rightPointData === 'string') {
      rightUrl = rightPointData === 'empty' ? this.EMPTY_IMAGE_URL : rightPointData;
    } else if (rightPointData && typeof rightPointData === 'object') {
      rightUrl = rightPointData.image_360 === 'empty' ? this.EMPTY_IMAGE_URL : (rightPointData.image_360 || null);
      rightThumb = rightPointData.image_360_thumb || undefined;
      rightTiles = rightPointData.tiles;
      rightTileSourceMode = rightPointData.tileSourceMode ?? rightTileSourceMode;
    }

    // Parse left viewer data
    if (typeof leftPointData === 'string') {
      leftUrl = leftPointData === 'empty' ? null : leftPointData;
    } else if (leftPointData && typeof leftPointData === 'object') {
      leftUrl = leftPointData.image_360 === 'empty' ? null : (leftPointData.image_360 || null);
      leftThumb = leftPointData.image_360_thumb || undefined;
      leftTiles = leftPointData.tiles;
      leftTileSourceMode = leftPointData.tileSourceMode ?? leftTileSourceMode;
    }

    // Load right viewer
    if (rightUrl) {
      if (!rightTiles || rightTiles.length === 0) {
        // For single images without tiles, use progressive loading with thumbnail if available
        if (rightThumb && rightThumb !== rightUrl) {
          await this.rightViewer.loadSingleImageWithThumb(rightThumb, rightUrl, rightAngle);
        } else {
          await this.rightViewer.loadImage(rightUrl, rightAngle);
        }
      } else {
        await this.rightViewer.loadImage(rightUrl, rightAngle, rightTiles, rightTileSourceMode);
      }
    }

    const isSplitScreen = ThreeSixtyViewer.viewerMode === ViewerMode.SPLIT_SCREEN;
    let leftAngle: { verticalAngle?: number; horizontalAngle?: number } | null = null;

    if (leftUrl) {
      const baseOrientation = rightUrl ? rightAngle : this.rightViewer.getCameraOrientation();
      leftAngle = leftOffset
        ? {
            verticalAngle: this.rightViewer.getCameraOrientation()?.verticalAngle || 0,
            horizontalAngle:
              (this.rightViewer.getCameraOrientation()?.horizontalAngle || 0) + (leftOffset || 0),
          }
        : baseOrientation ?? null;
    }

    if (isSplitScreen) {
      await this.loadLeftViewerImage(
        leftUrl,
        leftThumb,
        leftTiles,
        leftTileSourceMode,
        leftAngle,
      );
      this.pendingLeftImage = null;
    } else {
      if (leftUrl) {
        this.pendingLeftImage = {
          url: leftUrl,
          thumb: leftThumb,
          tiles: leftTiles,
          tileSourceMode: leftTileSourceMode,
          angle: leftAngle,
        };
        const fallbackOrientation = this.rightViewer.getCameraOrientation();
        const finalAngle = leftAngle ?? fallbackOrientation;
        if (
          finalAngle &&
          finalAngle.horizontalAngle !== undefined &&
          finalAngle.verticalAngle !== undefined
        ) {
          this.leftViewer.update({
            horizontalAngle: finalAngle.horizontalAngle,
            verticalAngle: finalAngle.verticalAngle,
          });
        }
      } else {
        this.pendingLeftImage = null;
        this.leftViewer.update(this.rightViewer.getCameraOrientation());
      }
    }

    if (callback) {
      callback();
    }
  }

  private async loadLeftViewerImage(
    url: string | null,
    thumb: string | undefined,
    tiles: Array<{ level: number; cols_per_face: number; rows_per_face: number }> | undefined,
    tileSourceMode: 'standard' | 'blurred',
    angle: { verticalAngle?: number; horizontalAngle?: number } | null,
  ): Promise<void> {
    if (!url) {
      this.leftViewer.update(this.rightViewer.getCameraOrientation());
      return;
    }

    const normalizedAngle = angle ?? undefined;

    if (!tiles || tiles.length === 0) {
      if (thumb && thumb !== url) {
        await this.leftViewer.loadSingleImageWithThumb(thumb, url, normalizedAngle);
      } else {
        await this.leftViewer.loadImage(url, normalizedAngle);
      }
      return;
    }

    await this.leftViewer.loadImage(url, normalizedAngle, tiles, tileSourceMode);
  }

  private async loadPendingLeftImageIfNeeded(): Promise<void> {
    if (!this.pendingLeftImage) {
      return;
    }

    const { url, thumb, tiles, tileSourceMode = 'standard', angle } = this.pendingLeftImage;
    await this.loadLeftViewerImage(url, thumb, tiles, tileSourceMode, angle);
    this.pendingLeftImage = null;
  }
  public getCameraOrientation() {
    return this.rightViewer.getCameraOrientation();
  }
  /**
   * Calculates position for a new observation based on mouse event and invokes callback.
   * @param event - The mouse event.
   * @param newObservationImage
   * @returns Calculated horizontal and vertical angles for observation positioning.
   */
  public addNewObservation(
    event: MouseEvent,
    newObservationImage: (rawImage: string) => void,
  ): { horizontalAngle: number; verticalAngle: number; isRightViewer: boolean } {
    return this.mouseController.addNewObservation(event, newObservationImage);
  }

  /**
   * Initializes observations on the left and right viewers.
   * @param observations - Array of ObservationRefactor objects.
   * @param observationClickCallback - Callback function to execute on observation click.
   */
  public initObservations(
    observations: ViewerObservation[],
    observationClickCallback: (id: string) => void,
    isSplit: boolean,
  ): void {
    const leftObservations: ViewerObservation[] = [];
    observations?.forEach((observation) => {
      leftObservations.push(
        new ViewerObservation(
          observation.position,
          observation.id,
          observation.observationData,
          observation.color,
          observation.status,
        ),
      );
    });
    if (!isSplit) {
      this.rightViewer.initObservations(observations, (id: string) => {
        observationClickCallback(id);
        // to make sure call onMouseUp event after click
        setTimeout(() => {
          this.rightViewer.onMouseUp();
          this.leftViewer.onMouseUp();
        }, 100);
      });
    } else {
      this.leftViewer.initObservations(leftObservations, (id: string) => {
        observationClickCallback(id);
        // to make sure call onMouseUp event after click
        setTimeout(() => {
          this.rightViewer.onMouseUp();
          this.leftViewer.onMouseUp();
        }, 100);
      });
    }
  }
  public initWalkthroughes(
    walkthroughes: Walkthrough[],
    walkthroughClickCallback: (id: string) => void,
  ): void {
    this.rightViewer.initWalkthroughes(walkthroughes, (id: string) => {
      walkthroughClickCallback(id);
      // to make sure call onMouseUp event after click
      setTimeout(() => {
        this.rightViewer.onMouseUp();
        this.leftViewer.onMouseUp();
      }, 100);
    });
  }
  public addCameraMoveListener(listener: onCameraMoveCallback, isRightViewer: boolean): void {
    if (isRightViewer) this.rightViewer.addCameraMoveListener(listener);
    else this.leftViewer.addCameraMoveListener(listener);
  }
  public removeCameraMoveListener(listener: onCameraMoveCallback, isRightViewer: boolean): void {
    if (isRightViewer) this.rightViewer.removeCameraMoveListener(listener);
    else this.leftViewer.removeCameraMoveListener(listener);
  }
  public addCameraZoomListener(listener: onCameraZoomCallback, isRightViewer: boolean): void {
    if (isRightViewer) this.rightViewer.addCameraZoomListener(listener);
    else this.leftViewer.addCameraZoomListener(listener);
  }
  public removeCameraZoomListener(listener: onCameraZoomCallback, isRightViewer: boolean): void {
    if (isRightViewer) this.rightViewer.removeCameraZoomListener(listener);
    else this.leftViewer.removeCameraZoomListener(listener);
  }

  /**
   * Setup LOD synchronization system
   */
  private setupLODSynchronization(): void {
    // Create LOD change callback for right viewer
    this.rightLODChangeCallback = (oldLevel: number, newLevel: number) => {
      if (this.lodSyncEnabled && ThreeSixtyViewer.viewerMode === ViewerMode.SPLIT_SCREEN) {
        // Fire and forget - don't block the callback chain
        this.syncLODFromRightToLeft(newLevel).catch(() => {
          // Silently handle sync errors
        });
      }
    };
  }

  /**
   * Enable LOD synchronization between viewers
   */
  private enableLODSync(): void {
    this.lodSyncEnabled = true;
    
    // Add LOD change listener to right viewer
    if (this.rightLODChangeCallback) {
      this.rightViewer.addLODChangeCallback(this.rightLODChangeCallback);
    }
  }

  /**
   * Disable LOD synchronization between viewers
   */
  private disableLODSync(): void {
    this.lodSyncEnabled = false;
    
    // Remove LOD change listener from right viewer
    if (this.rightLODChangeCallback) {
      this.rightViewer.removeLODChangeCallback(this.rightLODChangeCallback);
    }
  }

  /**
   * Synchronize LOD from right viewer to left viewer
   */
  private async syncLODFromRightToLeft(targetLevel: number): Promise<void> {
    try {
      // Check if both viewers have the same source
      const rightSource = this.rightViewer.getSourceKey();
      const leftSource = this.leftViewer.getSourceKey();
      
      if (!rightSource || !leftSource || rightSource !== leftSource) {
        return; // Different sources, no sync needed
      }

      // Get current left viewer LOD
      const leftCurrentLOD = this.leftViewer.getCurrentLODLevel();
      
      // Only sync if levels are different
      if (leftCurrentLOD.level !== targetLevel) {
        // Set the LOD level on left viewer
        this.leftViewer.setLODLevel(targetLevel);
        
        // Force viewport update to trigger tile loading immediately
        // Use a small delay to ensure LOD level is properly set
        await new Promise(resolve => setTimeout(resolve, 50));
        await this.triggerLeftViewerTileUpdate();
      }
    } catch (error) {
      // Silently handle sync errors
    }
  }

  /**
   * Manual LOD synchronization (for debugging or forced sync)
   */
  private async syncLODIfSameSource(): Promise<void> {
    if (!this.lodSyncEnabled || ThreeSixtyViewer.viewerMode !== ViewerMode.SPLIT_SCREEN) {
      return;
    }

    try {
      const rightLOD = this.rightViewer.getCurrentLODLevel();
      await this.syncLODFromRightToLeft(rightLOD.level);
    } catch (error) {
      // Silently handle sync errors
    }
  }

  /**
   * Force left viewer to update tiles for current viewport
   */
  private async triggerLeftViewerTileUpdate(): Promise<void> {
    try {
      // Get left viewer's current viewport and trigger tile loading
      if (this.leftViewer.updateTilesForCurrentViewport) {
        await this.leftViewer.updateTilesForCurrentViewport();
      }
    } catch (error) {
      // Silently handle update errors
    }
  }
  /**
   * Sets the visibility of observations across viewers.
   * @param isVisible - Boolean indicating whether observations should be visible.
   */
  public setObservationVisibility(isVisible: boolean): void {
    if (isVisible) {
      ThreeSixtyViewer.isObservationVisible = true;
      this.rightViewer.showObservations();
      this.leftViewer.showObservations();
    } else {
      ThreeSixtyViewer.isObservationVisible = false;
      this.rightViewer.hideObservations();
      this.leftViewer.hideObservations();
    }
  }
  public filterObservationsByStatus(status: string) {
    this.rightViewer.filterObservationsByStatus(status);
    this.leftViewer.filterObservationsByStatus(status);
  }

  public toggleBimSScreen(): void {
    if (ThreeSixtyViewer.viewerMode === ViewerMode.NORMAL) {
      ThreeSixtyViewer.viewerMode = ViewerMode.HALF_SCREEN;
      this.resizeWindow();
    } else {
      ThreeSixtyViewer.viewerMode = ViewerMode.NORMAL;
      this.resizeWindow();
    }
  }
  /**
   * Toggles split-screen mode and adjusts rendering.
   */
  public async toggleSplitScreen(): Promise<void> {
    ThreeSixtyViewer.viewerMode =
      ThreeSixtyViewer.viewerMode === ViewerMode.NORMAL
        ? ViewerMode.SPLIT_SCREEN
        : ViewerMode.NORMAL;
    this.resizeWindow();

    if (ThreeSixtyViewer.viewerMode === ViewerMode.SPLIT_SCREEN) {
      this.rightViewer.update();

      // Don't call update on leftViewer here as it might trigger tile loading
      // before an image is loaded. The left viewer will be properly initialized
      // when the split map loads an image via loadImage()

      // Enable LOD synchronization for split-screen
      this.enableLODSync();

      // The left viewer's camera and tiles will be set up when
      // handleSplitMapInitialization loads an image
      await this.loadPendingLeftImageIfNeeded();
      
      // After loading pending left image, check loading state
      // This ensures the loading panel clears if right viewer is not loading
      this.checkAndUpdateLoadingState();
    } else {
      this.disableLODSync();
      
      const leftTileManager = (this.leftViewer as any).tileManager;
      leftTileManager?.cancelAllLoads?.();
      
      this.checkAndUpdateLoadingState();
    }
  }

  /**
   * Locks the camera position between left and right viewers.
   */
  public lockScreens(): void {
    if (ThreeSixtyViewer.isLocked) {
      this.leftViewer.unlock(this.rightViewer.getCameraTarget());
    }
    ThreeSixtyViewer.isLocked = !ThreeSixtyViewer.isLocked;
  }

  public setViewerActive(isActive: boolean) {
    if (isActive) {
      this.mouseController.enable();
    } else {
      this.mouseController.disable();
    }
  }
  /**
   * Sets up event listeners, including resize handling.
   */
  private setupEventListeners(): void {
    window.addEventListener('resize', this.resizeWindow.bind(this), false);
  }
  public setDefaultCameraOrientation(
    rightScreenAngle: { horizontalAngle: number; verticalAngle: number },
    leftScreenAngle: { horizontalAngle: number; verticalAngle: number } = null,
  ) {
    this.rightViewer.setDefaultCameraOrientation(rightScreenAngle);
    if (leftScreenAngle) this.leftViewer.setDefaultCameraOrientation(leftScreenAngle);
  }
  /**
   * Adjusts the renderer and viewer sizes on window resize.
   */
  public resizeWindow(): void {
    let aspect: number;
    const halfWidth = window.innerWidth / 2;
    const fullHeight = window.innerHeight;

    switch (ThreeSixtyViewer.viewerMode) {
      case ViewerMode.HALF_SCREEN:
        aspect = halfWidth / fullHeight;
        this.renderer.setSize(halfWidth, fullHeight);
        this.rightViewer.resize(aspect);
        this.leftViewer.disable();
        break;
      case ViewerMode.SPLIT_SCREEN:
        aspect = halfWidth / fullHeight;
        this.renderer.setSize(window.innerWidth, fullHeight);
        this.leftViewer.resize(aspect);
        this.rightViewer.resize(aspect);
        this.leftViewer.enable();

        break;
      case ViewerMode.NORMAL:
        aspect = window.innerWidth / fullHeight;
        this.renderer.setSize(window.innerWidth, fullHeight);
        this.leftViewer.disable();

        this.rightViewer.resize(aspect);
        break;
    }
  }

  /**
   * Runs the main animation loop.
   */
  private animate(): void {
    requestAnimationFrame(() => this.animate());
    this.renderer.setScissorTest(true);

    switch (ThreeSixtyViewer.viewerMode) {
      case ViewerMode.HALF_SCREEN:
        this.renderViewer(0, 0, window.innerWidth / 2, window.innerHeight, this.rightViewer);

        break;
      case ViewerMode.SPLIT_SCREEN:
        this.renderViewer(0, 0, window.innerWidth / 2, window.innerHeight, this.leftViewer);
        this.renderViewer(
          window.innerWidth / 2,
          0,
          window.innerWidth / 2,
          window.innerHeight,
          this.rightViewer,
        );
        break;
      case ViewerMode.NORMAL:
        this.renderViewer(0, 0, window.innerWidth, window.innerHeight, this.rightViewer);
        break;
    }

    this.renderer.setScissorTest(false);
  }

  /**
   * Renders a specified viewer within defined screen boundaries.
   * @param x - X-coordinate for rendering.
   * @param y - Y-coordinate for rendering.
   * @param width - Width of the viewport.
   * @param height - Height of the viewport.
   * @param viewer - Viewer to render (left or right).
   */
  private renderViewer(
    x: number,
    y: number,
    width: number,
    height: number,
    viewer: ViewerScreen,
  ): void {
    this.renderer.setScissor(x, y, width, height);
    this.renderer.setViewport(x, y, width, height);

    viewer.animate();
  }

  /**
   * Cleans up resources when the viewer is destroyed.
   */
  public destroy(): void {
    // Clean up LOD synchronization
    this.disableLODSync();
    
    this.leftViewer.onDestroy();
    this.rightViewer.onDestroy();
    window.removeEventListener('resize', this.resizeWindow.bind(this), false);
    this.mouseController.disable();
    this.renderer.dispose();
    this.container.removeChild(this.renderer.domElement);
    ThreeSixtyViewer.viewerMode = ViewerMode.NORMAL;
    ThreeSixtyViewer.isObservationVisible = true;
    ThreeSixtyViewer.isLocked = false;
  }

  public getImageEffects() {
    return this.rightViewer.getImageEffects();
  }

  public setWatermark(name?: string, email?: string, client?: string): void {

    this.watermarkText = {
      name: name || this.watermarkText.name,
      email: email || this.watermarkText.email,
      client: client !== undefined ? client : this.watermarkText.client,
    };
    this.leftViewer.setWatermarkText(
      this.watermarkText.name,
      this.watermarkText.email,
      this.watermarkText.client,
    );
    this.rightViewer.setWatermarkText(
      this.watermarkText.name,
      this.watermarkText.email,
      this.watermarkText.client,
    );
  }

  public setWatermarkEnabled(isEnabled: boolean): void {
    this.watermarkEnabled = isEnabled;
    this.leftViewer.setWatermarkEnabled(isEnabled);
    this.rightViewer.setWatermarkEnabled(isEnabled);
  }

  /**
   * Takes a screenshot of the current view and returns it as a blob.
   * @param callback - Optional callback function to execute after screenshot is completed
   * @returns Promise that resolves with the image blob
   */
  public getScreenshot(callback?: () => void): Promise<Blob> {
    return new Promise((resolve, reject) => {
      // Store current layer visibility states
      const rightElementsLayer = this.rightViewer['layerManager'].getLayer(LayerType.ELEMENTS);
      const leftElementsLayer = this.leftViewer['layerManager'].getLayer(LayerType.ELEMENTS);
      const wasRightElementsVisible = rightElementsLayer.visible;
      const wasLeftElementsVisible = leftElementsLayer.visible;

      // Hide elements layer for screenshot
      rightElementsLayer.visible = false;
      leftElementsLayer.visible = false;

      if (ThreeSixtyViewer.viewerMode === ViewerMode.SPLIT_SCREEN) {
        // Store current renderer state
        const currentViewport = this.renderer.getViewport(new THREE.Vector4());
        const currentScissor = this.renderer.getScissor(new THREE.Box2());
        const currentScissorTest = this.renderer.getScissorTest();

        // Enable scissor test
        this.renderer.setScissorTest(true);

        // Render left view
        this.renderer.setViewport(0, 0, window.innerWidth / 2, window.innerHeight);
        this.renderer.setScissor(0, 0, window.innerWidth / 2, window.innerHeight);
        const leftComposer = this.leftViewer.getImageEffects();
        if (leftComposer) {
          leftComposer.render();
        } else {
          this.renderer.render(this.leftViewer.scene, this.leftViewer.camera);
        }

        // Render right view
        this.renderer.setViewport(
          window.innerWidth / 2,
          0,
          window.innerWidth / 2,
          window.innerHeight,
        );
        this.renderer.setScissor(
          window.innerWidth / 2,
          0,
          window.innerWidth / 2,
          window.innerHeight,
        );
        const rightComposer = this.rightViewer.getImageEffects();
        if (rightComposer) {
          rightComposer.render();
        } else {
          this.renderer.render(this.rightViewer.scene, this.rightViewer.camera);
        }

        // Restore renderer state
        this.renderer.setViewport(currentViewport);
        this.renderer.setScissor(currentScissor);
        this.renderer.setScissorTest(currentScissorTest);
      } else {
        // Normal mode - just capture the right viewer
        const composer = this.rightViewer.getImageEffects();
        if (composer) {
          composer.render();
        } else {
          this.renderer.render(this.rightViewer.scene, this.rightViewer.camera);
        }
      }

      this.renderer.domElement.toBlob((blob) => {
        // Restore layer visibility states
        rightElementsLayer.visible = wasRightElementsVisible;
        leftElementsLayer.visible = wasLeftElementsVisible;

        if (!blob) {
          reject(new Error('Failed to generate screenshot blob'));
          return;
        }

        // Execute callback if provided
        if (callback) {
          callback();
        }

        resolve(blob);
      }, 'image/png');
    });
  }

  /**
   * Check both viewers and update overall loading state
   * Only track right viewer for main loading callbacks to avoid split-screen left viewer
   * from blocking the loading panel dismissal
   */
  /**
   * Handle base loading state change from right viewer's tile manager
   * This is for the main loading panel (only during initial base tile load)
   */
  private handleBaseLoadingChange(isLoading: boolean): void {
    const previousBaseLoading = this.baseLoading;
    this.baseLoading = isLoading;
    if (previousBaseLoading !== isLoading) {
      this.notifyBaseLoadingCallbacks(isLoading);
    }
  }

  /**
   * Handle any loading state change from right viewer's tile manager
   * This is for the white spinner (during any tile loading)
   */
  private handleAnyLoadingChange(isLoading: boolean): void {
    const previousTextureLoading = this.texturesLoading;
    this.texturesLoading = isLoading;
    if (previousTextureLoading !== isLoading) {
      this.notifyTextureLoadingCallbacks(isLoading);
    }
  }

  /**
   * Check both viewers and update overall loading state (legacy fallback)
   */
  private checkAndUpdateLoadingState(): void {
    const rightViewerTileManager = (this.rightViewer as any).tileManager;

    const rightBaseLoading = rightViewerTileManager?.isLoading?.() || false;
    const rightAnyLoading = rightViewerTileManager?.isAnyLoading?.() ?? rightBaseLoading;

    this.handleBaseLoadingChange(rightBaseLoading);
    this.handleAnyLoadingChange(rightAnyLoading);
  }

  /**
   * Setup loading state tracking for tile managers
   * Connects to TileManager callbacks to track loading states
   */
  private setupLoadingStateTracking(): void {
    let rightConnected = false;

    const connectToTileManager = () => {
      const rightViewerTileManager = (this.rightViewer as any).tileManager;
      
      if (rightViewerTileManager && !rightConnected) {
        rightConnected = true;
        
        rightViewerTileManager.onLoadingStateChange((isLoading: boolean) => {
          this.handleBaseLoadingChange(isLoading);
        });
        
        if (typeof rightViewerTileManager.onAnyLoadingStateChange === 'function') {
          rightViewerTileManager.onAnyLoadingStateChange((isLoading: boolean) => {
            this.handleAnyLoadingChange(isLoading);
          });
        }
      }
    };

    connectToTileManager();

    const checkInterval = setInterval(() => {
      connectToTileManager();
      if (rightConnected) {
        clearInterval(checkInterval);
      }
    }, 500);

    setTimeout(() => clearInterval(checkInterval), 30000);
  }

  /**
   * Notify all loading callbacks
   */
  private notifyBaseLoadingCallbacks(isLoading: boolean): void {
    this.baseLoadingCallbacks.forEach(callback => {
      try {
        callback(isLoading);
      } catch (error) {
        // Silently handle callback errors
      }
    });
  }

  private notifyTextureLoadingCallbacks(isLoading: boolean): void {
    this.textureLoadingCallbacks.forEach(callback => {
      try {
        callback(isLoading);
      } catch (error) {
        // Silently handle callback errors
      }
    });
  }

  /**
   * Add callback for texture loading state changes
   */
  public onLoadingStateChange(callback: (isLoading: boolean) => void): void {
    this.baseLoadingCallbacks.add(callback);
  }

  /**
   * Remove loading state change callback
   */
  public removeLoadingStateCallback(callback: (isLoading: boolean) => void): void {
    this.baseLoadingCallbacks.delete(callback);
  }

  public onTextureLoadingStateChange(callback: (isLoading: boolean) => void): void {
    this.textureLoadingCallbacks.add(callback);
  }

  public removeTextureLoadingStateCallback(callback: (isLoading: boolean) => void): void {
    this.textureLoadingCallbacks.delete(callback);
  }

  /**
   * Get current texture loading state
   */
  public isLoadingTextures(): boolean {
    return this.texturesLoading;
  }

  /**
   * Get loading information from both viewers
   */
  public getLoadingInfo(): { 
    isLoading: boolean; 
    leftLoading: boolean; 
    rightLoading: boolean;
    leftPendingLoads: number;
    rightPendingLoads: number;
  } {
    const leftLoading = (this.leftViewer as any).tileManager?.isAnyLoading?.() || false;
    const rightLoading = (this.rightViewer as any).tileManager?.isAnyLoading?.() || false;
    const leftPendingLoads = (this.leftViewer as any).tileManager?.getPendingLoadsCount() || 0;
    const rightPendingLoads = (this.rightViewer as any).tileManager?.getPendingLoadsCount() || 0;
    
    return {
      isLoading: this.texturesLoading,
      leftLoading,
      rightLoading,
      leftPendingLoads,
      rightPendingLoads,
    };
  }
}
